import React, {Component} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import Popover from 'react-popover';
import {Trans} from '@lingui/macro';

import EditIcon from '../Icons/EditIcon';
import CompletedIcon from '../Icons/CompletedIcon';
import LockIcon from '../Icons/LockIcon';
import ArrowRightIcon from '../Icons/ArrowRightIcon';
import ContinueIcon from '../Icons/ContinueIcon';
import TestItemIcon from '../TestItemIcon';
import TestItemStatus from '../TestItemStatus';

import {getIsLegacyBrowser} from '../../reducers/ui';
import {i18n} from '../../services/i18n';
import {extractTranslatedLabel} from '../../services/utilities';

import cl from 'classnames';
import s from './TestItem.css';

const defaultDelay = 100;
const avatarStyle = {width: '60px', height: '60px', marginLeft: '0.5em'};
const avatarContainerStyle = {marginLeft: '1.5em'};

class TestItem extends Component {
  state = {
    isPopoverOpen: false,
    isLegacyBrowserPopoverOpen: false,
  };

  hoverTimer = null;

  onChangeSelection = (isSelecting = true) => () => {
    const {selectTest, deselectTest, delay} = this.props;
    const realDelay = Number.isInteger(delay) ? delay : defaultDelay;

    this.hoverTimer = setTimeout(isSelecting ? selectTest : deselectTest, realDelay);
  };

  onSelect = this.onChangeSelection(true);
  onDeselect = this.onChangeSelection(false);

  stopTimer = () => {
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }
  };

  hasPrerequisites = () => {
    const {missingPrerequisites, status, isAssessor} = this.props;

    return (
      status !== 'incomplete' &&
      status !== 'complete' &&
      missingPrerequisites &&
      missingPrerequisites.size > 0 &&
      !isAssessor
    );
  };

  onMouseEnter = () => {
    const {isCompleted, repeatable, isLegacyBrowser, supportsLegacyBrowsers} = this.props;
    const hasPrerequisites = this.hasPrerequisites();
    const available =
      (!isCompleted || Boolean(repeatable)) &&
      !hasPrerequisites &&
      (!isLegacyBrowser || supportsLegacyBrowsers);

    if (available) {
      this.onSelect();
    } else {
      this.onDeselect();
    }

    if (hasPrerequisites) {
      this.setState({isPopoverOpen: true});
    }
  };

  onMouseLeave = () => {
    this.stopTimer();

    const hasPrerequisites = this.hasPrerequisites();
    if (hasPrerequisites) {
      this.setState({isPopoverOpen: false});
    }
  };

  onClick = () => {
    const {
      onClick,
      isMobile,
      selectTest,
      isCompleted,
      repeatable,
      isLegacyBrowser,
      supportsLegacyBrowsers,
    } = this.props;
    const hasPrerequisites = this.hasPrerequisites();

    const available =
      (!isCompleted || Boolean(repeatable)) &&
      !hasPrerequisites &&
      (!isLegacyBrowser || supportsLegacyBrowsers);

    if (!available) {
      return;
    }

    if (isMobile) {
      onClick();
    }

    selectTest();
  };

  onLegacyBrowserIconClick = () => {
    this.setState(oldState => ({
      isLegacyBrowserPopoverOpen: !oldState.isLegacyBrowserPopoverOpen,
    }));
  };

  getAriaLabel = () => {
    const {label, status} = this.props;

    switch (status) {
      case 'incomplete':
        return `${label} ${i18n._('status.incomplete', null, {
          defaults: 'incompleto',
        })}`;
      case 'complete':
        return `${label} ${i18n._('status.complete', null, {
          defaults: 'completo',
        })}`;
      default:
        return `${label} ${i18n._('status.not_started', null, {
          defaults: 'non iniziato',
        })}`;
    }
  };

  render() {
    const {
      iconLabel,
      label,
      isActive,
      isCompleted,
      missingPrerequisites,
      status,
      repeatable,
      lang,
      isMobile,
      isLegacyBrowser,
      supportsLegacyBrowsers,
    } = this.props;
    const {isPopoverOpen, isLegacyBrowserPopoverOpen} = this.state;
    const hasPrerequisites = this.hasPrerequisites();

    return (
      <Popover
        isOpen={isPopoverOpen}
        preferPlace="left"
        body={
          <p className={s.Popover}>
            <Trans id="test_item.label_1">Concludi</Trans>{' '}
            <span className={s.MissingPrerequisitesList}>
              {missingPrerequisites ? (
                missingPrerequisites
                  .map(test => extractTranslatedLabel(test, lang, 'name'))
                  .toArray()
                  .join(', ')
              ) : (
                <div />
              )}
            </span>{' '}
            <Trans id="test_item.label_2">per sbloccare questo task</Trans>
          </p>
        }
      >
        <button
          className={cl(
            s.TestItem,
            isActive ? s.Active : null,
            isCompleted || (isLegacyBrowser && !supportsLegacyBrowsers) ? s.Completed : null,
            hasPrerequisites && s.Locked
          )}
          onMouseEnter={this.onMouseEnter}
          onMouseLeave={this.onMouseLeave}
          onTouchStart={this.onMouseEnter}
          onBlur={this.onMouseLeave}
          onClick={this.onClick}
          aria-label={this.getAriaLabel()}
        >
          {isActive ? (
            <ArrowRightIcon width={'5.938em'} customColor={'white'} />
          ) : (
            <div className={s.Divider} />
          )}
          <TestItemIcon
            className={s.ItemAvatar}
            iconLabel={iconLabel}
            isActive={isActive}
            isCompleted={isCompleted}
            isLocked={hasPrerequisites}
            size={50}
            avatarStyle={avatarStyle}
            avatarContainerStyle={{
              ...avatarContainerStyle,
              marginLeft: isMobile ? '0.8em' : '1.5em',
            }}
          />
          <span className={cl(s.ItemName, isActive ? s.ItemActive : null)}>{label}</span>
          {!isActive ? (
            <div className={s.Icons}>
              {isLegacyBrowser && !supportsLegacyBrowsers ? (
                <Popover
                  isOpen={isLegacyBrowserPopoverOpen}
                  preferPlace="above"
                  body={
                    <p className={s.Popover}>
                      <Trans id="test_item.legacy">
                        La prova non è disponibile per il tuo browser, per usufruirne usa una
                        versiona aggiorna di Chrome, Firefox, Edge o Safari
                      </Trans>
                    </p>
                  }
                >
                  <TestItemStatus
                    size={30}
                    icon={
                      <button
                        className={s.LegacyIconButton}
                        onClick={this.onLegacyBrowserIconClick}
                      >
                        <LockIcon />
                      </button>
                    }
                  />
                </Popover>
              ) : status === 'incomplete' ? (
                <TestItemStatus size={30} icon={<ContinueIcon />} />
              ) : null}
              {status === 'complete' && repeatable ? (
                <TestItemStatus size={30} icon={<EditIcon />} />
              ) : null}
              {status === 'complete' ? <TestItemStatus size={30} icon={<CompletedIcon />} /> : null}
              {hasPrerequisites ? <TestItemStatus size={30} icon={<LockIcon />} /> : null}
            </div>
          ) : null}
        </button>
      </Popover>
    );
  }
}

TestItem.propTypes = {
  iconLabel: PropTypes.string,
  label: PropTypes.string,
  isActive: PropTypes.bool,
  isCompleted: PropTypes.bool,
  selectTest: PropTypes.func,
  startTest: PropTypes.func,
  status: PropTypes.string,
  isLegacyBrowser: PropTypes.bool,
};

function mapStateToProps(state) {
  const isLegacyBrowser = getIsLegacyBrowser(state);

  return {
    isLegacyBrowser,
  };
}

export default connect(mapStateToProps)(TestItem);
